import { GraduationCap, Settings } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useToast } from "@/shared/hooks/use-toast";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import {
  coachingService,
  type CoachingScenarioItem,
  type CoachingTaskItem,
  type ScenarioCreateRequest,
  type TaskCreateRequest,
  type TaskUpdateRequest,
} from "../api/coachingService";
import { tagService } from "@/entities/tag";
import { EMPTY_FORM, type ScenarioFormData } from "../ui/ai-peilian/ScenarioFormDialog";
import { CoachingSettingsDrawer } from "../ui/ai-peilian/CoachingSettingsDrawer";
import { SummaryCards } from "../ui/ai-peilian/SummaryCards";
import { TaskEditDialog } from "../ui/ai-peilian/TaskEditDialog";
import { EvaluationDetailDialog } from "../ui/ai-peilian/EvaluationDetailDialog";
import { TasksTab } from "../ui/ai-peilian/TasksTab";
import { EvaluationsTab } from "../ui/ai-peilian/EvaluationsTab";
import { ScenariosTab } from "../ui/ai-peilian/ScenariosTab";
import { TaskDetailDrawer } from "../ui/ai-peilian/TaskDetailDrawer";
import { useAuth } from "@/contexts/AuthContext";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { useOrgScopeFilter } from "@/shared/hooks/useOrgScopeFilter";
import { useClampedPage } from "@/shared/hooks/useClampedPage";
import { EvaluationTaskDetailDrawer } from "../ui/ai-peilian/EvaluationTaskDetailDrawer";
import { TranscriptDialog } from "../ui/ai-peilian/TranscriptDialog";
import { errorToast } from "@/shared/lib/apiErrors";
import { normalizeDifficulty } from "../ui/ai-peilian/helpers";
import { WeComRequiredBanner } from "@/entities/wecom-app/ui/WeComRequiredBanner";
import { useWeComModuleBound } from "@/entities/wecom-app/model/useWeComModuleBound";
import { taskCreatedDescKey } from "../lib/taskCreatedToast";

function scenarioToForm(sc: CoachingScenarioItem): ScenarioFormData {
  return {
    name: sc.name,
    difficulty: normalizeDifficulty(sc.difficulty) ?? "medium",
    mode: sc.mode ?? "roleplay",
    interactionMode: sc.interactionMode ?? "text",
    description: sc.description ?? "",
    maxRounds: String(sc.maxRounds ?? 8),
    positions: sc.positions ?? "",
    customerPersona: sc.customerPersona ?? "",
    productContext: sc.productContext ?? "",
    knowledgeTags: sc.knowledgeTags ?? "",
    dimensions: sc.dimensions ?? [],
    kbWeight: sc.kbWeight ?? 0.3,
    aiConfig: sc.aiConfig ?? { voice: null, staffAware: false },
    customerRoleKey: sc.customerRoleKey ?? null,
    customerToneKey: sc.customerToneKey ?? null,
    status: sc.status ?? "active",
    productCodes: sc.productCodes ?? [],
    // 声音 V2:编辑回填(老场景/老后端无键 → null=普通话/不指定)。漏了这两行,
    // 编辑一次场景就会把存过的口音/风格静默洗掉 —— 复制/回填家族的老坑。
    voiceDialect: sc.voiceDialect ?? null,
    voiceStyle: sc.voiceStyle ?? null,
  };
}

export default function AiPeilian() {
  const { t } = useTranslation("coaching");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "tasks";
  const { toast } = useToast();

  const [scenarioFormOpen, setScenarioFormOpen] = useState(false);
  const [editingScenario, setEditingScenario] = useState<CoachingScenarioItem | null>(null);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<CoachingTaskItem | null>(null);
  const [viewingEvalId, setViewingEvalId] = useState<number | null>(null);
  const [taskSearch, setTaskSearch] = useState("");
  const [evalSearch, setEvalSearch] = useState("");
  const [evalSortKey, setEvalSortKey] = useState<"default" | "avgDesc" | "passAsc" | "riskDesc">(
    "default"
  );

  // Phase β: 发卡 + 任务详情
  const [detailTaskId, setDetailTaskId] = useState<number | null>(null);
  // 评估考核 Tab 的任务详情抽屉(独立于 TasksTab 的进度抽屉,有 PDF 操作)
  const [evalDetailTaskId, setEvalDetailTaskId] = useState<number | null>(null);
  // 任务详情 → 员工行 → 对话记录 dialog 的 evaluationId
  const [transcriptEvalId, setTranscriptEvalId] = useState<number | null>(null);

  // Sprint 7: 设置抽屉
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data: coachingStats } = useQuery({
    queryKey: ["coaching-stats"],
    queryFn: coachingService.getStats,
  });
  // #9 (2026-07-05) — 组织级归属筛选(共享单元,替代旧「只看我的」toggle),MANAGER+ 可切
  const { user } = useAuth();
  const canSwitchScope = isTenantManagerPlus(user?.role);
  const canManage = canSwitchScope; // MANAGER+ 写门(场景写/设置;对齐后端 @PreAuthorize PD-2,STAFF 隐藏)
  // F 分页补齐:切组织 scope 时清 tpage 回第 1 页(codex P2:否则窄 scope 下旧 tpage 存活 → 现空页)。
  const orgFilter = useOrgScopeFilter({ canSwitchScope, resetKeys: ["tpage"] });
  const { scopeParams } = orgFilter;
  // 任务列表服务端分页(独立 URL key `tpage`,不与 tab 撞)。
  const taskPage = Math.max(1, Number(searchParams.get("tpage") ?? "1") || 1);
  const setTaskPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "tasks");
    if (p <= 1) next.delete("tpage");
    else next.set("tpage", String(p));
    setSearchParams(next);
  };
  const { data: tasksPage, isSuccess: tasksLoaded } = useQuery({
    queryKey: ["coaching-tasks", scopeParams, taskPage],
    queryFn: () => coachingService.listTasks({ ...scopeParams, page: taskPage, size: 20 }),
    enabled: tab === "tasks",
    placeholderData: (prev) => prev,
  });
  const tasks = tasksPage?.records ?? [];
  const tasksTotal = tasksPage?.total ?? 0;
  // 分页搁浅纠偏:删末页最后一行 / 数据变少使 tpage 越界时 clamp 回末页(server 分页无客户端 slice 兜底)。
  // 1 基:maxPage = ceil(total/size);enabled 守 tasksLoaded 防首屏 total=0 抹掉深链 tpage。
  useClampedPage({
    page: taskPage,
    maxPage: Math.max(1, Math.ceil(tasksTotal / 20)),
    setPage: setTaskPage,
    enabled: tab === "tasks" && tasksLoaded,
  });
  const { data: scenarios = [] } = useQuery({
    queryKey: ["coaching-scenarios"],
    queryFn: coachingService.listScenarios,
    enabled: tab === "scenarios" || tab === "tasks",
  });
  const { data: evaluationTasks = [] } = useQuery({
    queryKey: ["coaching-evaluations-by-task"],
    queryFn: coachingService.listEvaluationsByTask,
    enabled: tab === "evaluations",
  });
  const { data: tagDefs = [] } = useQuery({
    queryKey: ["kb-tags"],
    queryFn: () => tagService.list(),
  });
  const tagNameMap = useMemo(() => {
    const map = new Map<string, string>();
    tagDefs.forEach((t) => map.set(t.tagValue, t.name));
    return map;
  }, [tagDefs]);

  const createScenarioMutation = useMutationWithToast({
    mutationFn: (data: ScenarioCreateRequest) => coachingService.createScenario(data),
    successToast: t("toast.scenarioCreated"),
    errorToast: t("toast.scenarioCreateFailed"),
    invalidateKeys: [["coaching-scenarios"]],
    onSuccess: () => setScenarioFormOpen(false),
  });
  const updateScenarioMutation = useMutationWithToast({
    mutationFn: ({ id, data }: { id: number; data: ScenarioCreateRequest }) =>
      coachingService.updateScenario(id, data),
    successToast: t("toast.scenarioUpdated"),
    errorToast: t("toast.scenarioUpdateFailed"),
    invalidateKeys: [["coaching-scenarios"]],
    onSuccess: () => setEditingScenario(null),
  });
  const deleteScenarioMutation = useMutationWithToast({
    mutationFn: (id: number) => coachingService.deleteScenario(id),
    successToast: t("toast.scenarioDeleted"),
    errorToast: t("toast.scenarioDeleteFailed"),
    invalidateKeys: [["coaching-scenarios"]],
  });
  // 1006:没绑企微时卡片发不出去,提示不能说「已发到企业微信」
  const coachingWecomBound = useWeComModuleBound("coaching");
  const createTaskMutation = useMutationWithToast({
    mutationFn: (data: TaskCreateRequest) => coachingService.createTask(data),
    // 1005:派完任务告诉管理员员工去哪练(电脑浏览器打不开练习)
    successToast: {
      title: t("toast.taskCreated"),
      description: t(taskCreatedDescKey(coachingWecomBound)),
    },
    errorToast: t("toast.taskCreateFailed"),
    invalidateKeys: [["coaching-tasks"]],
    onSuccess: () => setTaskFormOpen(false),
  });
  const deleteTaskMutation = useMutationWithToast({
    mutationFn: (id: number) => coachingService.deleteTask(id),
    successToast: t("toast.taskDeleted"),
    errorToast: t("toast.taskDeleteFailed"),
    invalidateKeys: [["coaching-tasks"]],
  });
  const updateTaskMutation = useMutationWithToast({
    mutationFn: ({ id, data }: { id: number; data: TaskUpdateRequest }) =>
      coachingService.updateTask(id, data),
    successToast: t("toast.taskUpdated"),
    errorToast: t("toast.taskUpdateFailed"),
    invalidateKeys: [["coaching-tasks"]],
    onSuccess: () => setEditingTask(null),
  });
  const handleDownloadReport = async (evalId: number) => {
    try {
      await coachingService.downloadReport(evalId);
    } catch (err) {
      toast({
        title: t("toast.downloadFailed"),
        description: errorToast(err).description,
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <TaskEditDialog
        task={editingTask}
        onClose={() => setEditingTask(null)}
        onSubmit={(id, data) => updateTaskMutation.mutate({ id, data })}
        isPending={updateTaskMutation.isPending}
      />

      <EvaluationDetailDialog evalId={viewingEvalId} onClose={() => setViewingEvalId(null)} />

      {/* Sprint 4 删除独立 LaunchTrainingDialog;补发/加人/提醒在 TaskDetailDrawer 内做。 */}

      <TaskDetailDrawer
        taskId={detailTaskId}
        onClose={() => setDetailTaskId(null)}
        onShowTranscript={(id) => setTranscriptEvalId(id)}
        canManage={canManage}
      />

      <EvaluationTaskDetailDrawer
        taskId={evalDetailTaskId}
        onClose={() => setEvalDetailTaskId(null)}
        onView={setViewingEvalId}
        onDownload={handleDownloadReport}
      />

      <TranscriptDialog evalId={transcriptEvalId} onClose={() => setTranscriptEvalId(null)} />

      <CoachingSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <div className="space-y-5">
        <div className="flex items-center gap-3 fx-enter">
          <div className="p-2.5 rounded-lg bg-primary/10">
            <GraduationCap className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">{t("page.title")}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{t("page.subtitle")}</p>
          </div>
        </div>

        <WeComRequiredBanner module="coaching" />

        <SummaryCards stats={coachingStats} />

        <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="tasks">{t("tabs.tasks")}</TabsTrigger>
              <TabsTrigger value="scenarios">{t("tabs.scenarios")}</TabsTrigger>
              <TabsTrigger value="evaluations">{t("tabs.evaluations")}</TabsTrigger>
            </TabsList>
            {canManage && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSettingsOpen(true)}
                title={t("page.settingsTooltip")}
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
          </div>

          <TabsContent value="tasks">
            <TasksTab
              tasks={tasks}
              scenarios={scenarios}
              search={taskSearch}
              onSearchChange={setTaskSearch}
              createOpen={taskFormOpen}
              onOpenCreate={() => setTaskFormOpen(true)}
              onCloseCreate={() => setTaskFormOpen(false)}
              onCreateTask={(data) => createTaskMutation.mutate(data)}
              isCreating={createTaskMutation.isPending}
              onEdit={setEditingTask}
              onDelete={(id) => deleteTaskMutation.mutate(id)}
              isDeleting={deleteTaskMutation.isPending}
              onShowDetail={setDetailTaskId}
              canManage={canManage}
              orgFilter={orgFilter}
              total={tasksTotal}
              page={taskPage}
              onPageChange={setTaskPage}
            />
          </TabsContent>

          <TabsContent value="evaluations">
            <EvaluationsTab
              tasks={evaluationTasks}
              search={evalSearch}
              onSearchChange={setEvalSearch}
              sortKey={evalSortKey}
              onSortChange={setEvalSortKey}
              onShowDetail={setEvalDetailTaskId}
            />
          </TabsContent>

          <TabsContent value="scenarios">
            <ScenariosTab
              canManage={canManage}
              scenarios={scenarios}
              tagNameMap={tagNameMap}
              formOpen={scenarioFormOpen}
              editingScenario={editingScenario}
              initialForm={editingScenario ? scenarioToForm(editingScenario) : EMPTY_FORM}
              onOpenCreate={() => {
                setEditingScenario(null);
                setScenarioFormOpen(true);
              }}
              onEdit={setEditingScenario}
              onCloseForm={() => {
                setScenarioFormOpen(false);
                setEditingScenario(null);
              }}
              onSubmit={(data) => {
                if (editingScenario) {
                  updateScenarioMutation.mutate({ id: editingScenario.id, data });
                } else {
                  createScenarioMutation.mutate(data);
                }
              }}
              isSubmitting={createScenarioMutation.isPending || updateScenarioMutation.isPending}
              onDelete={(id) => deleteScenarioMutation.mutate(id)}
              isDeleting={deleteScenarioMutation.isPending}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
